import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { getEcole } from 'app/entities/ecole/ecole.reducer';
import { defaultValue, IBulletinBranding } from './bulletin-branding.model';

const apiUrl = '/api/ecoles';

const initialState = {
  entity: defaultValue,
  logoPreview: null as string | null,
  signaturePreview: null as string | null,
  professeurPrincipalSignaturePreview: null as string | null,
  logoPreviewEcoleId: null as string | null,
  signaturePreviewEcoleId: null as string | null,
  professeurPrincipalSignaturePreviewEcoleId: null as string | null,
  loading: false,
  updating: false,
  updateSuccess: false,
  errorMessage: null as string | null,
};

const toDataUrl = (data: ArrayBuffer, contentType: string) => {
  const binary = Array.from(new Uint8Array(data))
    .map(charCode => String.fromCharCode(charCode))
    .join('');
  return `data:${contentType};base64,${window.btoa(binary)}`;
};

const getImage = async (url: string) => {
  try {
    const response = await axios.get<ArrayBuffer>(url, { responseType: 'arraybuffer' });
    return toDataUrl(response.data, response.headers['content-type'] || 'image/png');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getBulletinBranding = createAsyncThunk(
  'bulletinBranding/fetch',
  async (ecoleId: string) => (await axios.get<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding`)).data,
  { serializeError: serializeAxiosError }
);

export const updateBulletinBranding = createAsyncThunk(
  'bulletinBranding/update',
  async ({ ecoleId, headerText, footerText }: { ecoleId: string; headerText: string | null; footerText: string | null }) =>
    (await axios.put<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding`, { headerText, footerText })).data,
  { serializeError: serializeAxiosError }
);

export const getBulletinLogo = createAsyncThunk(
  'bulletinBranding/fetchLogo',
  async (ecoleId: string) => getImage(`${apiUrl}/${ecoleId}/logo`),
  { serializeError: serializeAxiosError }
);

export const getBulletinSignature = createAsyncThunk(
  'bulletinBranding/fetchSignature',
  async (ecoleId: string) => getImage(`${apiUrl}/${ecoleId}/bulletin-branding/signature`),
  { serializeError: serializeAxiosError }
);

export const getBulletinProfesseurPrincipalSignature = createAsyncThunk(
  'bulletinBranding/fetchProfesseurPrincipalSignature',
  async (ecoleId: string) => getImage(`${apiUrl}/${ecoleId}/bulletin-branding/professeur-principal-signature`),
  { serializeError: serializeAxiosError }
);

export const uploadBulletinLogo = createAsyncThunk(
  'bulletinBranding/uploadLogo',
  async ({ ecoleId, file }: { ecoleId: string; file: FormData }, thunkAPI) => {
    await axios.post(`${apiUrl}/${ecoleId}/logo`, file, { headers: { 'content-type': 'multipart/form-data' } });
    thunkAPI.dispatch(getEcole(ecoleId));
    thunkAPI.dispatch(getBulletinLogo(ecoleId));
    return (await axios.get<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding`)).data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteBulletinLogo = createAsyncThunk(
  'bulletinBranding/deleteLogo',
  async (ecoleId: string, thunkAPI) => {
    await axios.delete(`${apiUrl}/${ecoleId}/logo`);
    thunkAPI.dispatch(getEcole(ecoleId));
    return (await axios.get<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding`)).data;
  },
  { serializeError: serializeAxiosError }
);

export const uploadBulletinSignature = createAsyncThunk(
  'bulletinBranding/uploadSignature',
  async ({ ecoleId, file }: { ecoleId: string; file: FormData }, thunkAPI) => {
    const response = await axios.post<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding/signature`, file, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getBulletinSignature(ecoleId));
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteBulletinSignature = createAsyncThunk(
  'bulletinBranding/deleteSignature',
  async (ecoleId: string) => (await axios.delete<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding/signature`)).data,
  { serializeError: serializeAxiosError }
);

export const uploadBulletinProfesseurPrincipalSignature = createAsyncThunk(
  'bulletinBranding/uploadProfesseurPrincipalSignature',
  async ({ ecoleId, file }: { ecoleId: string; file: FormData }, thunkAPI) => {
    const response = await axios.post<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding/professeur-principal-signature`, file, {
      headers: { 'content-type': 'multipart/form-data' },
    });
    thunkAPI.dispatch(getBulletinProfesseurPrincipalSignature(ecoleId));
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteBulletinProfesseurPrincipalSignature = createAsyncThunk(
  'bulletinBranding/deleteProfesseurPrincipalSignature',
  async (ecoleId: string) =>
    (await axios.delete<IBulletinBranding>(`${apiUrl}/${ecoleId}/bulletin-branding/professeur-principal-signature`)).data,
  { serializeError: serializeAxiosError }
);

export type BulletinBrandingState = Readonly<typeof initialState>;

const BulletinBrandingSlice = createSlice({
  name: 'bulletinBranding',
  initialState: initialState as BulletinBrandingState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(getBulletinBranding.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })
      .addCase(getBulletinLogo.fulfilled, (state, action) => {
        if (state.logoPreviewEcoleId === action.meta.arg) {
          state.logoPreview = action.payload;
        }
      })
      .addCase(getBulletinSignature.fulfilled, (state, action) => {
        if (state.signaturePreviewEcoleId === action.meta.arg) {
          state.signaturePreview = action.payload;
        }
      })
      .addCase(getBulletinProfesseurPrincipalSignature.fulfilled, (state, action) => {
        if (state.professeurPrincipalSignaturePreviewEcoleId === action.meta.arg) {
          state.professeurPrincipalSignaturePreview = action.payload;
        }
      })
      .addCase(updateBulletinBranding.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = action.payload;
      })
      .addCase(uploadBulletinLogo.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
      })
      .addCase(deleteBulletinLogo.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
        state.logoPreview = null;
      })
      .addCase(uploadBulletinSignature.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
      })
      .addCase(deleteBulletinSignature.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
        state.signaturePreview = null;
      })
      .addCase(uploadBulletinProfesseurPrincipalSignature.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
      })
      .addCase(deleteBulletinProfesseurPrincipalSignature.fulfilled, (state, action) => {
        state.updating = false;
        state.entity = action.payload;
        state.professeurPrincipalSignaturePreview = null;
      })
      .addMatcher(isPending(getBulletinBranding), state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getBulletinLogo), (state, action) => {
        state.logoPreviewEcoleId = action.meta.arg;
        state.logoPreview = null;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getBulletinSignature), (state, action) => {
        state.signaturePreviewEcoleId = action.meta.arg;
        state.signaturePreview = null;
        state.errorMessage = null;
      })
      .addMatcher(isPending(getBulletinProfesseurPrincipalSignature), (state, action) => {
        state.professeurPrincipalSignaturePreviewEcoleId = action.meta.arg;
        state.professeurPrincipalSignaturePreview = null;
        state.errorMessage = null;
      })
      .addMatcher(
        isPending(
          updateBulletinBranding,
          uploadBulletinLogo,
          deleteBulletinLogo,
          uploadBulletinSignature,
          deleteBulletinSignature,
          uploadBulletinProfesseurPrincipalSignature,
          deleteBulletinProfesseurPrincipalSignature
        ),
        state => {
          state.updating = true;
          state.updateSuccess = false;
          state.errorMessage = null;
        }
      )
      .addMatcher(isRejected(getBulletinBranding), (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Erreur de chargement';
      })
      .addMatcher(isRejected(getBulletinLogo), (state, action) => {
        if (state.logoPreviewEcoleId === action.meta.arg) {
          state.logoPreview = null;
        }
      })
      .addMatcher(isRejected(getBulletinSignature), (state, action) => {
        if (state.signaturePreviewEcoleId === action.meta.arg) {
          state.signaturePreview = null;
        }
      })
      .addMatcher(isRejected(getBulletinProfesseurPrincipalSignature), (state, action) => {
        if (state.professeurPrincipalSignaturePreviewEcoleId === action.meta.arg) {
          state.professeurPrincipalSignaturePreview = null;
        }
      })
      .addMatcher(
        isRejected(
          updateBulletinBranding,
          uploadBulletinLogo,
          deleteBulletinLogo,
          uploadBulletinSignature,
          deleteBulletinSignature,
          uploadBulletinProfesseurPrincipalSignature,
          deleteBulletinProfesseurPrincipalSignature
        ),
        (state, action) => {
          state.updating = false;
          state.errorMessage = action.error.message || 'Erreur de mise à jour';
        }
      );
  },
});

export const { reset } = BulletinBrandingSlice.actions;
export default BulletinBrandingSlice.reducer;
